import { useState, useRef, useCallback, useEffect } from "react"
import type { FrontMatterValues } from "@/types/frontMatter"

export type SaveStatus = "new" | "unsaved" | "saving" | "saved" | "transcribing"

interface UseAutoSaveOptions {
  filePath: string | null
  delay?: number
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function encodeDropboxPath(path: string): string {
  return path
    .split("/")
    .map((s) => encodeURIComponent(s))
    .join("/")
    .replace(/^\//, "")
}

export function useAutoSave({ filePath, delay = 1000 }: UseAutoSaveOptions) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(filePath ? "saved" : "new")
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestDataRef = useRef<{ frontMatter: FrontMatterValues; body: string } | null>(null)

  const save = useCallback(
    async (frontMatter: FrontMatterValues, body: string) => {
      if (!filePath) return

      setSaveStatus("saving")
      try {
        await fetch(`/api/files/${encodeDropboxPath(filePath)}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({ front_matter: frontMatter, body }),
        })
        setSaveStatus("saved")
      } catch {
        setSaveStatus("unsaved")
      }
    },
    [filePath]
  )

  const markUnsaved = useCallback(
    (frontMatter: FrontMatterValues, body: string) => {
      setSaveStatus("unsaved")
      latestDataRef.current = { frontMatter, body }

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        if (latestDataRef.current) {
          save(latestDataRef.current.frontMatter, latestDataRef.current.body)
        }
      }, delay)
    },
    [save, delay]
  )

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return { saveStatus, markUnsaved, save }
}
